import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { flagConfigForRussianUsers, isCountryBlacklisted } from '@atlassian/amkt-common';
import { makeAnalyticsData, MarketplaceAnalyticsContext, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { getCurrentGeoIPRegion, isFreeApp } from '@atlassian/mpac-api-client';
import { StatsigContext, useFeatureFlags, useStatsig } from '@atlassian/mpac-feature-flags';
import { getOfferingIdFromEligibleOfferings } from '@atlassian/mpac-primitives';
import Telemetry from '@atlassian/mpac-telemetry';
import { BillingSystem, makeUserId } from '@atlassian/mpac-types';
import { FlagsImplementationProvider } from '@atlassian/mpac-ui';
import { appSupportsEditions, getSentryClient, initSentry, log } from '@atlassian/mpac-utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import version from '../../../../../../version';
import AnalyticsClient from '../../../../analytics/Client';
import { ANALYTICS_SUBJECT_ID_RUSSIAN_USER_BLOCKED, AppInstallBlockedForRussianUserEvent } from '../../../../analytics/Events';
import { initializeBackends } from '../../../../analytics/Send';
import { toSPIInstallableAddonIdentifier } from '../../../../records/InstallableAppIdentifier';
import { INSTRUCTIONAL_INSTALL_MODE } from '../../../../records/InstallMode/InstructionalInstallMode';
import getSPI, { initSPI } from '../../../../services/SPI';
import { AppInstallTask } from '../../../../telemetry/tasks';
import { installableAppIdentifiersAreEqual, trackAppInstallTelemetryTaskError } from '../../../../utils/app-installation';
import { useInitialiseStatsigIfNeeded } from '../../../hooks/useInitialiseStatsigIfNeeded';
import AppInstallDialog from '../../AppInstallDialog';
import { appInstallFlagKeys, getCCPBillingSystemErrorFlag, getInstallErrorFlagProps, getInstallInProgressFlagProps, getInstallSuccessFlagProps, getSimultaneousInstallErrorFlagProps } from '../../AppInstallFlags';
import { useInstallationLock } from '../../contexts/InstallationLockProvider';
import { TwoClickAppInstallFocusedTask } from '../../TwoClickAppInstall';
import { makeInstallableAppKeyIdentifier, makeMarketplaceAppKeyIdentifier } from '../../types';
import { getAppKeyFromInstallableAppIdentifier, getAppKeyFromInstallIdentifier } from '../../utils/appIdentifier';
import { useAppInstallAnalytics } from '../useAppInstallAnalytics';
import { useAppInstallBaseData } from '../useAppInstallBaseData';
import { useCurrentUser } from '../useCurrentUser';
import { useHostData } from '../useHostData';
import { useSelectedEditionType } from '../useSelectedEditionType';
import { jsx as _jsx } from "react/jsx-runtime";
const APP_INSTALLER_STATES = {
  INITIALIZED: 'INITIALIZED',
  DETERMINING: 'DETERMINING',
  AWAITING_USER_CONFIRMATION: 'AWAITING_USER_CONFIRMATION',
  INSTALL_IN_PROGRESS: 'INSTALL_IN_PROGRESS',
  INSTALL_SUCCESS: 'INSTALL_SUCCESS',
  INSTALL_FAILED: 'INSTALL_FAILED',
  INSTALL_DISMISSED: 'INSTALL_DISMISSED'
};

// eslint-disable-next-line max-lines-per-function -- hook has many inline functions to allow component state access
export function useMarketplaceAppInstall(spi, appInstallConfig, installLocation, vendorSupportTicketUrl, options) {
  var _baseData$result;
  const [installIdentifier, setInstallIdentifier] = useState(null);
  const [appInstallerState, setAppInstallerState] = useState(APP_INSTALLER_STATES.INITIALIZED);
  const {
    installationState,
    dispatch: installationStateDispatch
  } = useInstallationLock();
  const featureFlags = useFeatureFlags();
  const {
    enableGeoIPFromStoreBff,
    enableOneClickAppInstall,
    enableTwoClickAppInstall,
    enableSelfServeAppEditions,
    enableMILCustomerSupport
  } = useStatsig();
  const {
    hostInfo,
    hostConfig
  } = useHostData();
  const currentUser = useCurrentUser();
  const fireAnalyticsEvent = useAnalyticsEvents();
  const installFeedbackConfigRef = useRef();
  const oneClickAppInstallEnabled = enableOneClickAppInstall();
  const twoClickAppInstallEnabled = enableTwoClickAppInstall();
  const selfServeAppEditionsEnabled = enableSelfServeAppEditions();
  const {
    isClientInitialised: installDialogStatsigClientInitialised,
    statsigContext
  } = useInitialiseStatsigIfNeeded({
    hostInfo,
    environment: appInstallConfig.environment,
    currentUser
  });
  useEffect(() => {
    if (hostInfo && currentUser !== null && currentUser !== void 0 && currentUser.atlassianAccountId) {
      AnalyticsClient.init(appInstallConfig, hostConfig, hostInfo);
      initializeBackends(hostInfo, makeUserId(currentUser.atlassianAccountId));
    }
  }, [hostInfo, currentUser]);
  useEffect(() => {
    const isDev = appInstallConfig.environment === 'local' || appInstallConfig.environment === 'dev';
    const releaseVersion = "EMCEE_NATIVE_".concat(version).concat(isDev ? '-dev' : '');
    initSentry(_objectSpread(_objectSpread({}, appInstallConfig.sentryConfig), {}, {
      releaseVersion,
      setupGlobalListeners: false
    }));
  }, [appInstallConfig]);
  useMemo(() => initSPI(spi), [spi]);
  useEffect(() => {
    var _getSentryClient;
    if (installIdentifier === null) return;
    (_getSentryClient = getSentryClient()) === null || _getSentryClient === void 0 ? void 0 : _getSentryClient.setTag('installLocation', installLocation);
    return () => {
      var _getSentryClient2;
      (_getSentryClient2 = getSentryClient()) === null || _getSentryClient2 === void 0 ? void 0 : _getSentryClient2.setTag('installLocation', undefined);
    };
  }, [installLocation, installIdentifier]);
  const baseData = useAppInstallBaseData(appInstallConfig.mpacBaseUrl, appInstallConfig.graphQLGatewayUrl, appInstallConfig.environment, installIdentifier, hostInfo, installDialogStatsigClientInitialised);
  const {
    selectedEditionType,
    setSelectedEditionType,
    resetSelectedEditionType
  } = useSelectedEditionType({
    eligibleOfferings: baseData === null || baseData === void 0 ? void 0 : (_baseData$result = baseData.result) === null || _baseData$result === void 0 ? void 0 : _baseData$result.eligibleOfferings,
    enableMILCustomerSupport: enableMILCustomerSupport(),
    enableSelfServeAppEditions: enableSelfServeAppEditions()
  });
  useEffect(() => {
    if (!oneClickAppInstallEnabled || !installationState.directInstallInitiated || installationState.installationInProgressAppKey || !baseData.result || !installIdentifier) {
      return;
    }
    onInstallClick(baseData.result.appIdentifier);
  }, [baseData, installIdentifier, installationState, oneClickAppInstallEnabled]);
  const {
    fireEvent,
    analyticsContext
  } = useAppInstallAnalytics({
    baseData,
    installLocation
  });
  const handleInstallDialogRendered = () => {
    setAppInstallerState(APP_INSTALLER_STATES.AWAITING_USER_CONFIRMATION);
    fireEvent.viewedScreen.awaitingUserConfirmation();
  };
  const handleAppInstallDialogClose = () => {
    setAppInstallerState(APP_INSTALLER_STATES.INSTALL_DISMISSED);
    if (twoClickAppInstallEnabled) {
      resetSelectedEditionType();
    }
  };
  const dismissInstallErrorFlag = appIdentifier => setTimeout(() => {
    var _installFeedbackConfi;
    return (_installFeedbackConfi = installFeedbackConfigRef.current) === null || _installFeedbackConfi === void 0 ? void 0 : _installFeedbackConfi.flags.dismiss(appInstallFlagKeys.installError(appIdentifier));
  }, 0);
  const showAppInstallInProgressStatus = (appIdentifier, appName) => {
    var _installFeedbackConfi2;
    (_installFeedbackConfi2 = installFeedbackConfigRef.current) === null || _installFeedbackConfi2 === void 0 ? void 0 : _installFeedbackConfi2.flags.show(getInstallInProgressFlagProps(appIdentifier, appName, flagId => {
      var _installFeedbackConfi3;
      return (_installFeedbackConfi3 = installFeedbackConfigRef.current) === null || _installFeedbackConfi3 === void 0 ? void 0 : _installFeedbackConfi3.flags.dismiss(flagId);
    }));
  };
  const showAppInstallSuccessStatus = (appIdentifier, appName, installedAddon) => {
    var _baseData$result2, _baseData$result3, _baseData$result3$hos, _baseData$result3$hos2, _installFeedbackConfi4, _installFeedbackConfi5, _installFeedbackConfi6;
    const isEditionsSupported = appSupportsEditions({
      editions: baseData === null || baseData === void 0 ? void 0 : (_baseData$result2 = baseData.result) === null || _baseData$result2 === void 0 ? void 0 : _baseData$result2.editions,
      billingSystem: baseData === null || baseData === void 0 ? void 0 : (_baseData$result3 = baseData.result) === null || _baseData$result3 === void 0 ? void 0 : (_baseData$result3$hos = _baseData$result3.hostData) === null || _baseData$result3$hos === void 0 ? void 0 : (_baseData$result3$hos2 = _baseData$result3$hos.hostLicense) === null || _baseData$result3$hos2 === void 0 ? void 0 : _baseData$result3$hos2.billingSystem
    });
    (_installFeedbackConfi4 = installFeedbackConfigRef.current) === null || _installFeedbackConfi4 === void 0 ? void 0 : _installFeedbackConfi4.flags.show(getInstallSuccessFlagProps(appIdentifier, appName, installedAddon, isEditionsSupported));
    (_installFeedbackConfi5 = installFeedbackConfigRef.current) === null || _installFeedbackConfi5 === void 0 ? void 0 : _installFeedbackConfi5.flags.dismiss(appInstallFlagKeys.noSimultaneousInstalls());
    (_installFeedbackConfi6 = installFeedbackConfigRef.current) === null || _installFeedbackConfi6 === void 0 ? void 0 : _installFeedbackConfi6.flags.dismiss(appInstallFlagKeys.inProgress(appIdentifier));
  };
  const showAppInstallFailureStatus = _ref => {
    var _installFeedbackConfi7, _installFeedbackConfi9, _installFeedbackConfi10;
    let {
      appIdentifier,
      installError,
      appName
    } = _ref;
    (_installFeedbackConfi7 = installFeedbackConfigRef.current) === null || _installFeedbackConfi7 === void 0 ? void 0 : _installFeedbackConfi7.flags.show(getInstallErrorFlagProps({
      appIdentifier,
      hostConfig,
      installError,
      featureFlags,
      appName,
      onDismiss: () => {
        var _installFeedbackConfi8;
        return (_installFeedbackConfi8 = installFeedbackConfigRef.current) === null || _installFeedbackConfi8 === void 0 ? void 0 : _installFeedbackConfi8.flags.dismiss(appInstallFlagKeys.installError(appIdentifier));
      },
      vendorSupportTicketUrl
    }));
    (_installFeedbackConfi9 = installFeedbackConfigRef.current) === null || _installFeedbackConfi9 === void 0 ? void 0 : _installFeedbackConfi9.flags.dismiss(appInstallFlagKeys.noSimultaneousInstalls());
    (_installFeedbackConfi10 = installFeedbackConfigRef.current) === null || _installFeedbackConfi10 === void 0 ? void 0 : _installFeedbackConfi10.flags.dismiss(appInstallFlagKeys.inProgress(appIdentifier));
  };
  const showSimultaneousInstallAttemptedErrorStatus = () => {
    var _installFeedbackConfi11;
    (_installFeedbackConfi11 = installFeedbackConfigRef.current) === null || _installFeedbackConfi11 === void 0 ? void 0 : _installFeedbackConfi11.flags.show(getSimultaneousInstallErrorFlagProps());
  };
  const showAppInstallationBlockageErrorStatus = appIdentifier => {
    var _installFeedbackConfi12;
    (_installFeedbackConfi12 = installFeedbackConfigRef.current) === null || _installFeedbackConfi12 === void 0 ? void 0 : _installFeedbackConfi12.flags.show(getCCPBillingSystemErrorFlag(appIdentifier));
  };
  const installInProgress = () => installationState.installationInProgressAppKey !== null;
  const isAppInstallationSupported = installMode => isFreeApp(installMode.installableApp.marketplaceAppDetails.paymentModel) || baseData.result.hostData.hostLicense.billingSystem === BillingSystem.HAMS || baseData.result.hostData.hostLicense.billingSystem === BillingSystem.CCP;
  const allowRequestFromRegion = async () => {
    try {
      var _geoDetailsFromAMKT;
      let geoDetailsFromAMKT = null;
      if (!enableGeoIPFromStoreBff()) {
        geoDetailsFromAMKT = await getCurrentGeoIPRegion(appInstallConfig.mpacBaseUrl);
      }
      if (isCountryBlacklisted(((_geoDetailsFromAMKT = geoDetailsFromAMKT) === null || _geoDetailsFromAMKT === void 0 ? void 0 : _geoDetailsFromAMKT.countryCode) || (options === null || options === void 0 ? void 0 : options.countryCode))) {
        fireAnalyticsEvent(makeAnalyticsData({
          eventType: AppInstallBlockedForRussianUserEvent,
          actionSubjectId: ANALYTICS_SUBJECT_ID_RUSSIAN_USER_BLOCKED
        }).toObject());
        log.info('useMarketplaceInstallApp', 'Prevented app install for Russia');
        return false;
      } else {
        return true;
      }
    } catch (error) {
      log.error('useMarketplaceAppInstall', "Failed to retrieve geoIP", {
        error
      });
      return true;
    }
  };
  const handleAppInstallClick = async installableAppIdentifier => {
    var _installFeedbackConfi14, _installFeedbackConfi15, _installFeedbackConfi16, _baseData$result4, _baseData$result5, _baseData$result5$hos, _baseData$result5$hos2, _baseData$result6;
    const {
      appIdentifier,
      installMode,
      hostData
    } = baseData.result;
    if (!installableAppIdentifiersAreEqual(installMode.installableAppId, installableAppIdentifier)) {
      log.error('useMarketplaceAppInstall', 'App requested to be installed differs from installMode data');
      return;
    }
    if (!isAppInstallationSupported(installMode)) {
      setAppInstallerState(APP_INSTALLER_STATES.INSTALL_FAILED);
      showAppInstallationBlockageErrorStatus(appIdentifier);
      return;
    }
    const appKey = getAppKeyFromInstallableAppIdentifier(appIdentifier);
    const appName = installMode.installableApp.details.name;
    installationStateDispatch({
      type: 'set_install_in_progress',
      payload: {
        appKey
      }
    });
    setAppInstallerState(APP_INSTALLER_STATES.INSTALL_IN_PROGRESS);
    const allowRegionRequest = await allowRequestFromRegion();
    if (!allowRegionRequest) {
      var _installFeedbackConfi13;
      handleAppInstallDialogClose();
      installationStateDispatch({
        type: 'clear_install_in_progress'
      });
      (_installFeedbackConfi13 = installFeedbackConfigRef.current) === null || _installFeedbackConfi13 === void 0 ? void 0 : _installFeedbackConfi13.flags.show(flagConfigForRussianUsers);
      return;
    }
    if (!((_installFeedbackConfi14 = installFeedbackConfigRef.current) !== null && _installFeedbackConfi14 !== void 0 && _installFeedbackConfi14.suppressProgressFlag)) {
      showAppInstallInProgressStatus(appKey, appName);
    }
    dismissInstallErrorFlag(appKey);
    AppInstallTask.start();
    (_installFeedbackConfi15 = installFeedbackConfigRef.current) === null || _installFeedbackConfi15 === void 0 ? void 0 : (_installFeedbackConfi16 = _installFeedbackConfi15.onInstallStarted) === null || _installFeedbackConfi16 === void 0 ? void 0 : _installFeedbackConfi16.call(_installFeedbackConfi15, {
      appKey,
      appName
    });
    const isEditionsSupported = appSupportsEditions({
      editions: baseData === null || baseData === void 0 ? void 0 : (_baseData$result4 = baseData.result) === null || _baseData$result4 === void 0 ? void 0 : _baseData$result4.editions,
      billingSystem: baseData === null || baseData === void 0 ? void 0 : (_baseData$result5 = baseData.result) === null || _baseData$result5 === void 0 ? void 0 : (_baseData$result5$hos = _baseData$result5.hostData) === null || _baseData$result5$hos === void 0 ? void 0 : (_baseData$result5$hos2 = _baseData$result5$hos.hostLicense) === null || _baseData$result5$hos2 === void 0 ? void 0 : _baseData$result5$hos2.billingSystem
    });
    const offeringId = getOfferingIdFromEligibleOfferings({
      eligibleOfferings: baseData === null || baseData === void 0 ? void 0 : (_baseData$result6 = baseData.result) === null || _baseData$result6 === void 0 ? void 0 : _baseData$result6.eligibleOfferings,
      edition: selectedEditionType === null || selectedEditionType === void 0 ? void 0 : selectedEditionType.value,
      selfServeAppEditionsEnabled,
      isEditionsSupported,
      isMILEnabled: enableMILCustomerSupport()
    });
    getSPI().installAddon({
      identifier: toSPIInstallableAddonIdentifier(appIdentifier),
      paymentModel: installMode.installableApp.marketplaceAppDetails.paymentModel,
      offeringId
    }, {
      contextId: hostData.hostInfo.globalContext.workspaceAri,
      billing: {
        system: baseData.result.hostData.hostLicense.billingSystem
      },
      productName: baseData.result.hostData.hostInfo.product,
      cmtEligible: installMode.installableApp.cloudMigrationTrial.isEligible
    }, {
      /* Flag passed to trigger the app subscribe flow instead of the app install flow for paid Forge apps which are installed
      on HAMS sites but are unsubscribed.
      A separate flag is not passed for CCP since the endpoint called for install and subscribe are the same in the case of CCP sites.
      */
      enableReactivateForgeAppFromEmcee: false
    }).then(installedApp => {
      var _installFeedbackConfi17, _installFeedbackConfi18;
      showAppInstallSuccessStatus(appKey, appName, installedApp);
      AppInstallTask.succeeded();
      installationStateDispatch({
        type: 'installation_success',
        payload: {
          installedApp
        }
      });
      setAppInstallerState(APP_INSTALLER_STATES.INSTALL_SUCCESS);
      fireEvent.installSucceeded(appKey);
      (_installFeedbackConfi17 = installFeedbackConfigRef.current) === null || _installFeedbackConfi17 === void 0 ? void 0 : (_installFeedbackConfi18 = _installFeedbackConfi17.onInstallSuccess) === null || _installFeedbackConfi18 === void 0 ? void 0 : _installFeedbackConfi18.call(_installFeedbackConfi17, installedApp);
    }).catch(error => {
      var _installFeedbackConfi19, _installFeedbackConfi20;
      log.error('useMarketplaceAppInstall', "Failed to install the App", {
        error,
        tags: {
          installLocation
        }
      });
      showAppInstallFailureStatus({
        appIdentifier: appKey,
        installError: error,
        appName
      });
      // Add error log to get the stack trace for the app install error code 12
      if (error.code === '12') {
        log.error('useMarketplaceAppInstall', "App install failed for app ".concat(appKey, " with error code ").concat(error.code));
      }
      trackAppInstallTelemetryTaskError(error);
      installationStateDispatch({
        type: 'clear_install_in_progress'
      });
      setAppInstallerState(APP_INSTALLER_STATES.INSTALL_FAILED);
      fireEvent.installFailed(appKey, error);
      (_installFeedbackConfi19 = installFeedbackConfigRef.current) === null || _installFeedbackConfi19 === void 0 ? void 0 : (_installFeedbackConfi20 = _installFeedbackConfi19.onInstallFailure) === null || _installFeedbackConfi20 === void 0 ? void 0 : _installFeedbackConfi20.call(_installFeedbackConfi19, {
        appKey,
        appName
      });
    }).finally(() => setInstallIdentifier(null));
  };
  const onInstallClick = installableAppIdentifier => {
    Telemetry.traceUFOPress('install-app');
    handleAppInstallClick(installableAppIdentifier);
  };
  const getInstallerElement = () => {
    var _installFeedbackConfi21, _installFeedbackConfi22;
    const showAppInstallDialog = appInstallerState === APP_INSTALLER_STATES.DETERMINING || appInstallerState === APP_INSTALLER_STATES.AWAITING_USER_CONFIRMATION || appInstallerState === APP_INSTALLER_STATES.INSTALL_IN_PROGRESS && ((_installFeedbackConfi21 = installFeedbackConfigRef.current) === null || _installFeedbackConfi21 === void 0 ? void 0 : _installFeedbackConfi21.suppressProgressFlag);
    const appKey = getAppKeyFromInstallIdentifier(installIdentifier);
    const isInstructionalApp = () => {
      var _baseData$result7;
      return (baseData === null || baseData === void 0 ? void 0 : (_baseData$result7 = baseData.result) === null || _baseData$result7 === void 0 ? void 0 : _baseData$result7.installMode.type) === INSTRUCTIONAL_INSTALL_MODE;
    };
    const installerElement = /*#__PURE__*/_jsx("div", {
      id: "marketplace-app-installer-root",
      children: showAppInstallDialog && ((_installFeedbackConfi22 = installFeedbackConfigRef.current) === null || _installFeedbackConfi22 === void 0 ? void 0 : _installFeedbackConfi22.flags) && /*#__PURE__*/_jsx(FlagsImplementationProvider, {
        value: {
          showFlag: installFeedbackConfigRef.current.flags.show,
          dismissFlag: installFeedbackConfigRef.current.flags.dismiss
        },
        children: /*#__PURE__*/_jsx(MarketplaceAnalyticsContext, _objectSpread(_objectSpread({}, analyticsContext), {}, {
          children: twoClickAppInstallEnabled && !isInstructionalApp() ? /*#__PURE__*/_jsx(TwoClickAppInstallFocusedTask, {
            handleFocusedTaskClose: handleAppInstallDialogClose,
            appKey: appKey,
            baseData: baseData,
            appInstallConfig: appInstallConfig,
            onInstallClick: onInstallClick,
            isInstallInProgress: installInProgress,
            installDialogStatsigClientInitialised: installDialogStatsigClientInitialised,
            selectedEditionType: selectedEditionType,
            setSelectedEditionType: setSelectedEditionType
          }) : /*#__PURE__*/_jsx(AppInstallDialog, {
            appInstallConfig: appInstallConfig,
            appKey: appKey,
            onClose: handleAppInstallDialogClose,
            onRendered: handleInstallDialogRendered,
            onInstallClick: onInstallClick,
            baseData: baseData,
            isInstallInProgress: installInProgress,
            selectedEditionType: selectedEditionType,
            setSelectedEditionType: setSelectedEditionType,
            installDialogStatsigClientInitialised: installDialogStatsigClientInitialised
          })
        }))
      })
    });
    return /*#__PURE__*/_jsx(StatsigContext.Provider, {
      value: {
        statsigClient: statsigContext.statsigClient,
        statsigState: installDialogStatsigClientInitialised
      },
      children: installerElement
    });
  };
  const handleAppInstallStart = function handleAppInstallStart(appKey, installFeedbackConfig) {
    let skipInstallDialog = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    installFeedbackConfigRef.current = installFeedbackConfig;
    if (installInProgress()) {
      showSimultaneousInstallAttemptedErrorStatus();
      return;
    }
    if (typeof appKey === 'string') {
      setInstallIdentifier(makeMarketplaceAppKeyIdentifier(appKey));
    } else {
      setInstallIdentifier(makeInstallableAppKeyIdentifier(appKey));
    }
    if (!skipInstallDialog) {
      setAppInstallerState(APP_INSTALLER_STATES.DETERMINING);
      fireEvent.viewedScreen.determining();
    }
  };
  return {
    installerElement: getInstallerElement(),
    installApp: handleAppInstallStart,
    installAppByInstallableIdentifier: handleAppInstallStart
  };
}